import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../base/dropdown-menu";
import { cn } from "../../../lib/utils";
import { Building2, Check, ChevronsUpDown } from "lucide-react";

export interface OrganizationSummary {
  id: string;
  name: string;
}

export interface OrganizationSwitcherProps {
  organizations: OrganizationSummary[];
  activeOrganizationId?: string;
  onSwitch: (organizationId: string) => void;
}

/**
 * Active-organization switcher for the sidebar. Shows the active org; when the user
 * belongs to more than one org it becomes a dropdown that calls `onSwitch`. Hidden
 * entirely when there are no organizations. Styled for the dark-indigo rail; the
 * dropdown itself renders in the app (light) theme via a portal.
 */
export function OrganizationSwitcher({
  organizations,
  activeOrganizationId,
  onSwitch,
}: OrganizationSwitcherProps) {
  if (!organizations || organizations.length === 0) return null;

  const active = organizations.find((o) => o.id === activeOrganizationId) ?? organizations[0];
  const single = organizations.length === 1;

  const trigger = (
    <button
      type="button"
      disabled={single}
      className={cn(
        "flex w-full items-center gap-2 rounded-md border border-sidebar-border bg-sidebar-accent/40 px-3 py-2 text-left font-display text-[13px] text-sidebar-accent-foreground transition-colors",
        !single &&
          "hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
      )}
    >
      <Building2 className="h-4 w-4 shrink-0 text-sidebar-foreground/60" />
      <span className="min-w-0 flex-1 truncate">{active?.name ?? "Organization"}</span>
      {!single && (
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/50" aria-hidden />
      )}
    </button>
  );

  if (single) {
    return <div className="px-3 pt-3">{trigger}</div>;
  }

  return (
    <div className="px-3 pt-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {organizations.map((org) => (
            <DropdownMenuItem key={org.id} onClick={() => onSwitch(org.id)} className="gap-2">
              <Check
                className={cn("h-4 w-4", org.id === active?.id ? "opacity-100" : "opacity-0")}
              />
              <span className="truncate">{org.name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
